import { minorUnitOf } from "./currency.ts";

/**
 * Money as a person reads it, and back again.
 *
 * Amounts cross the wire as decimal strings of minor units, and every client
 * has to turn them into something legible without ever touching a float. That
 * is the same job in the back office, the operator console and the till, so it
 * lives here with the rest of the money primitives rather than three times
 * over.
 */

/** Minor units to a plain decimal — no symbol, no grouping, for an input. */
export function toDecimal(amount: string, currency: string): string {
  const exponent = minorUnitOf(currency as "USD");
  const negative = amount.startsWith("-");
  const digits = (negative ? amount.slice(1) : amount).padStart(exponent + 1, "0");
  const whole = digits.slice(0, digits.length - exponent) || "0";
  const fraction = exponent === 0 ? "" : `.${digits.slice(digits.length - exponent)}`;
  return `${negative ? "-" : ""}${whole}${fraction}`;
}

/** A decimal a person typed, back to minor units. Never via a float. */
export function toMinorUnits(decimal: string, currency: string): string {
  const exponent = minorUnitOf(currency as "USD");
  const match = /^(-?)(\d*)(?:[.,](\d*))?$/.exec(decimal.trim());
  if (!match) throw new Error(`Not a number: ${decimal}`);
  const [, sign, whole = "0", fraction = ""] = match;
  const padded = fraction.slice(0, exponent).padEnd(exponent, "0");
  const value = `${whole || "0"}${padded}`.replace(/^0+(?=\d)/, "");
  return `${sign}${value || "0"}`;
}

/** Minor units as a person reads them: grouped, with the currency in front. */
export function formatMoney(amount: string, currency: string): string {
  const text = toDecimal(amount, currency);
  const [whole = "0", fraction] = text.replace("-", "").split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${amount.startsWith("-") ? "-" : ""}${currency} ${grouped}${fraction ? `.${fraction}` : ""}`;
}
